import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, MapPin, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

interface ProvaAgendada {
  id: string;
  data_prova: string;
  hora_prova: string;
  local_prova: string;
  observacoes_agendamento?: string;
  observacoes_resultado?: string;
  confirmada: boolean;
  data_agendamento: string;
  realizada?: boolean;
  data_realizacao?: string;
  hora_realizacao?: string;
  resultado_prova?: "aprovada" | "ajustes_necessarios" | "nao_realizada";
}

interface ProvaPedidoCardProps {
  provaAgendada?: ProvaAgendada;
}

const locaisMap: Record<string, string> = {
  atelier: "🏢 No Ateliê",
  cliente: "🏠 Casa do Cliente",
  video: "📱 Vídeo Chamada",
};

const resultadosMap: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  aprovada: { icon: "✅", color: "text-green-600", label: "Aprovada" },
  ajustes_necessarios: { icon: "⚠️", color: "text-yellow-600", label: "Ajustes Necessários" },
  nao_realizada: { icon: "❌", color: "text-red-600", label: "Não Realizada" },
};

export function ProvaPedidoCard({ provaAgendada }: ProvaPedidoCardProps) {
  if (!provaAgendada) {
    return null;
  }

  const dataFormatada = new Date(provaAgendada.data_prova).toLocaleDateString("pt-BR");
  const dataRealizacaoFormatada = provaAgendada.data_realizacao
    ? new Date(provaAgendada.data_realizacao).toLocaleDateString("pt-BR")
    : null;
  const resultado = provaAgendada.resultado_prova ? resultadosMap[provaAgendada.resultado_prova] : null;

  return (
    <Card className={`border-l-4 ${provaAgendada.realizada ? 'border-l-green-500 bg-green-50' : 'border-l-blue-500 bg-blue-50'}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <CheckCircle2 className={`w-4 h-4 ${provaAgendada.realizada ? 'text-green-600' : 'text-blue-600'}`} />
          📅 {provaAgendada.realizada ? 'Prova Realizada' : 'Prova Agendada'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Agendamento */}
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-2">AGENDAMENTO</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Data</p>
                <p className="font-medium">{dataFormatada}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Hora</p>
                <p className="font-medium">{provaAgendada.hora_prova}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3">
            <MapPin className="w-4 h-4 text-gray-500" />
            <div>
              <p className="text-xs text-gray-500">Local</p>
              <p className="font-medium">{locaisMap[provaAgendada.local_prova] || provaAgendada.local_prova}</p>
            </div>
          </div>

          {provaAgendada.observacoes_agendamento && (
            <div className="mt-3">
              <p className="text-xs text-gray-500">Observações</p>
              <p className="text-sm text-gray-700">{provaAgendada.observacoes_agendamento}</p>
            </div>
          )}
        </div>

        {/* Resultado da Prova (se realizada) */}
        {provaAgendada.realizada && (
          <div className="border-t pt-4">
            <p className="text-xs font-semibold text-gray-600 mb-2">REALIZAÇÃO</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Data</p>
                  <p className="font-medium">{dataRealizacaoFormatada}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Hora</p>
                  <p className="font-medium">{provaAgendada.hora_realizacao}</p>
                </div>
              </div>
            </div>

            {resultado && (
              <div className="flex items-center gap-2 mt-3 p-2 bg-white rounded">
                <span className="text-lg">{resultado.icon}</span>
                <div>
                  <p className="text-xs text-gray-500">Resultado</p>
                  <p className={`font-medium text-sm ${resultado.color}`}>{resultado.label}</p>
                </div>
              </div>
            )}

            {provaAgendada.observacoes_resultado && (
              <div className="mt-3">
                <p className="text-xs text-gray-500">Observações</p>
                <p className="text-sm text-gray-700">{provaAgendada.observacoes_resultado}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
